import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MarketTicker } from "@/components/MarketTicker";
import { SignalCard } from "@/components/SignalCard";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
} from "recharts";
import {
  ArrowRight, ShieldCheck, Cpu, Zap, TrendingUp, Target,
  Activity, Gem, CheckCircle2, Sparkles, BarChart3,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1767424412548-1a1ac7f4b9bc?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Landing() {
  const { user, openAuth } = useAuth();
  const nav = useNavigate();
  const [perf, setPerf] = useState(null);
  const [goldSignal, setGoldSignal] = useState(null);

  useEffect(() => {
    api.get("/performance").then(({ data }) => setPerf(data)).catch(() => {});
    api.get("/signals?category=forex").then(({ data }) => {
      const active = data.signals.find((s) => s.status === "active" && !s.locked) || data.signals[0];
      setGoldSignal(active);
    }).catch(() => {});
  }, []);

  const go = () => (user ? nav("/signals") : openAuth("register"));

  return (
    <div>
      {/* HERO */}
      <section className="relative qs-grid-bg pt-28 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 qs-fade-up">
              <div
                data-testid="hero-win-rate-badge"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-300 mb-6"
              >
                <span className="relative flex h-2 w-2">
                  <span className="qs-pulse absolute inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                VERIFIED 90.0% 30-DAY WIN RATE · AI AUDITED
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                AI-Powered <span className="text-gradient">BUY / SELL</span> Signals for Forex, Gold & Crypto
              </h1>
              <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
                QuantumSignal AI scans the markets 24/7, decoding price action, momentum and
                sentiment to deliver precise entries, stop-loss and take-profit levels — with
                the <span className="text-amber-400 font-semibold">world's sharpest Gold (XAU/USD) engine.</span>
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  data-testid="hero-cta-get-signals"
                  onClick={go}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-12 px-7 text-base font-semibold shadow-lg shadow-emerald-500/25"
                >
                  Get Live Signals <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  data-testid="hero-cta-performance"
                  variant="outline"
                  onClick={() => nav("/performance")}
                  className="border-slate-700 bg-slate-900/40 text-slate-200 hover:bg-slate-800 rounded-full h-12 px-7 text-base"
                >
                  <BarChart3 size={18} className="mr-2" /> View Track Record
                </Button>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                <HeroStat value="90.0%" label="Win Rate" />
                <HeroStat value={perf ? `+${perf.total_pips.toLocaleString()}` : "—"} label="Pips Captured" />
                <HeroStat value="12k+" label="Active Traders" />
              </div>
            </div>

            {/* Signal terminal preview */}
            <div className="lg:col-span-5 qs-fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="qs-card p-1.5">
                <div className="rounded-[0.85rem] bg-[#0B0F16] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="flex items-center gap-2 text-xs font-mono-ticker text-slate-400">
                      <Cpu size={14} className="text-cyan-400" /> AI SIGNAL TERMINAL
                    </span>
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                    </span>
                  </div>
                  {goldSignal ? (
                    <SignalCard signal={goldSignal} onUpgrade={() => nav("/pricing")} />
                  ) : (
                    <div className="h-64 rounded-xl bg-slate-900/50 animate-pulse" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketTicker />

      {/* GOLD FOCUS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24">
        <div className="qs-card p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 opacity-10"><Gem size={200} className="text-amber-400" /></div>
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm font-semibold mb-3">
                <Gem size={16} /> GOLD SPECIALIST
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                The best AI for trading <span className="text-amber-400">XAU/USD Gold</span>
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Gold is our edge. Our model is tuned on years of XAU/USD volatility, London/NY
                session dynamics and macro sentiment to call reversals and breakouts others miss.
              </p>
              <ul className="mt-6 space-y-3">
                {["Session-aware Gold entries (London & New York)", "Macro + DXY correlation sentiment layer", "Dynamic stop-loss sizing for Gold volatility"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 size={18} className="text-amber-400 shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <Button onClick={go} data-testid="gold-cta" className="mt-7 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full h-11 px-6">
                Unlock Gold Signals <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            <div>
              {perf && (
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-400">Equity Growth ($1,000 start)</span>
                    <span className="text-emerald-400 font-semibold text-sm">+{perf.roi_pct}% ROI</span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={perf.equity_curve}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 10 }} interval={6} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
                      <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, color: "#F8FAFC" }} />
                      <Area type="monotone" dataKey="balance" stroke="#10B981" strokeWidth={2} fill="url(#g1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES / PERFORMANCE BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-20">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-3">
          Why traders trust <span className="text-gradient">QuantumSignal AI</span>
        </h2>
        <p className="text-slate-400 text-center max-w-2xl mx-auto mb-12">
          Institutional-grade analysis, delivered in seconds. Every signal is explainable.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Feature icon={<Cpu />} title="Explainable AI" body="Claude-powered reasoning behind every BUY/SELL — see the technicals, patterns & sentiment." />
          <Feature icon={<Target />} title="Precise Levels" body="Exact entry, stop-loss and three take-profit targets with calculated risk/reward." />
          <Feature icon={<ShieldCheck />} title="Risk Managed" body="AI sizes stops to each instrument's volatility to protect your capital." />
          <Feature icon={<Zap />} title="Live Scanner" body="Run the AI scanner on demand and generate a fresh signal in seconds." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-24">
        <div className="qs-card p-10 lg:p-16 text-center relative overflow-hidden">
          <Sparkles className="mx-auto text-cyan-400 mb-4" size={32} />
          <h2 className="font-display text-3xl lg:text-4xl font-bold">Start trading with an AI edge today</h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">Join thousands of traders following verified signals across Forex, Gold and Crypto.</p>
          <Button onClick={go} data-testid="footer-cta" className="mt-7 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-12 px-8 text-base font-semibold">
            Get Started Free <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}

const HeroStat = ({ value, label }) => (
  <div>
    <div className="font-display text-2xl sm:text-3xl font-black text-slate-100 font-mono-ticker">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </div>
);

const Feature = ({ icon, title, body }) => (
  <div className="qs-card p-6 hover:-translate-y-1 transition-transform duration-200">
    <div className="h-11 w-11 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-4">
      {React.cloneElement(icon, { size: 22 })}
    </div>
    <h3 className="font-semibold text-lg mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
  </div>
);
